import React from 'react';
import { X } from 'lucide-react';

interface DetailDrawerProps {
  open: boolean;
  title: string;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  className?: string;
}

const joinClasses = (...classes: Array<string | undefined | false>) =>
  classes.filter(Boolean).join(' ');

const DetailDrawer: React.FC<DetailDrawerProps> = ({
  open,
  title,
  subtitle,
  meta,
  children,
  footer,
  onClose,
  closeLabel = 'Close detail drawer',
  className,
}) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="drawer-backdrop" role="presentation">
      <button className="drawer-scrim" type="button" aria-label={closeLabel} onClick={onClose} />
      <aside
        className={joinClasses('drawer-panel', 'detail-drawer', className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="detail-drawer-title"
      >
        <div className="drawer-header">
          <div>
            {meta ? <div className="drawer-meta">{meta}</div> : null}
            <h2 id="detail-drawer-title">{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button className="icon-button drawer-close" type="button" aria-label={closeLabel} onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="drawer-body">
          {children}
        </div>
        {footer ? <div className="drawer-footer">{footer}</div> : null}
      </aside>
    </div>
  );
};

export default DetailDrawer;
